import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import { EditorContent, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import { getMarkRange } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { DELETION, INSERTION, type TranscriptDoc } from '../utils/transcriptDoc';
import { Deletion, Insertion, placeCaret, trackDelete } from '../utils/transcriptMarks';
import type { TranscriptComment } from '../utils/transcriptComments';
import { timecode, timingForPassage, type Cue, type Timing } from '../utils/transcriptCues';
import { formatEditStamp } from '../utils/time';
interface TranscriptEditorProps {
  doc: TranscriptDoc;
  editable: boolean;
  /** Bumped when the document must be rebuilt from `doc` (load, save, restore). */
  resetKey: number;
  onChange: (doc: TranscriptDoc) => void;
  /** Comments whose passage is still present, drawn in the margin. */
  comments: TranscriptComment[];
  canComment: boolean;
  onComment: (input: {
    quote: string;
    quoteStart: number;
    quoteSeconds: number | null;
    body: string;
  }) => Promise<boolean>;
  /** Timings, when the transcript came from Descript. Absent is normal. */
  cues?: Cue[];
  /** The text those cues were measured against — not necessarily what is on screen. */
  cueText?: string;
}
const EDITOR_CLASS = 'min-h-[38rem] w-full whitespace-pre-wrap border border-rule bg-paperDeep px-6 py-6 font-display text-xl leading-relaxed text-charcoal focus:outline-none sm:px-9 sm:py-8 sm:text-2xl';

/** The readable text of each paragraph — struck wording excluded. */
function paragraphTexts(editor: Editor): string[] {
  const out: string[] = [];
  editor.state.doc.forEach(node => {
    let text = '';
    node.forEach(child => {
      if (!child.isText) return;
      if (child.marks.some(mark => mark.type.name === DELETION)) return;
      text += child.text ?? '';
    });
    out.push(text);
  });
  return out;
}

/** Which paragraph a comment belongs beside, or -1 when it cannot be placed. */
function paragraphFor(paragraphs: string[], comment: TranscriptComment): number {
  if (!comment.quote) return -1;
  let offset = 0;
  let fallback = -1;
  for (let index = 0; index < paragraphs.length; index += 1) {
    const at = paragraphs[index].indexOf(comment.quote);
    if (at !== -1) {
      if (fallback === -1) fallback = index;
      if (comment.quoteStart >= 0 && Math.abs(offset + at - comment.quoteStart) < 60) return index;
    }
    offset += paragraphs[index].length + 1;
  }
  return fallback;
}
interface Marker {
  paragraph: number;
  top: number;
  comments: TranscriptComment[];
}

/** A timecode in the margin, beside the paragraph it belongs to. */
interface Stamp {
  paragraph: number;
  top: number;
  label: string;
}
interface Bubble {
  top: number;
  left: number;
  quote: string;
  quoteStart: number;
  /** When the selection was said, or null when the transcript has no timings. */
  timing: Timing | null;
  /** The selection is entirely struck wording, so the action is Restore. */
  struck: boolean;
}

/** The timecode on a saved comment, or '' when it has none. */
function stampOf(comment: TranscriptComment): string {
  return comment.quoteSeconds == null ? '' : timecode(comment.quoteSeconds);
}

/**
 * The transcript, as a structured document.
 *
 * Deleting never removes Portal's words: the selection is marked deleted and
 * stays exactly where it is, faded and struck through, from the moment the key
 * is pressed. Wording the reader added themselves deletes normally, because it
 * was never part of the delivered transcript.
 *
 * SELECTING A PASSAGE ASKS FOR A CHANGE (30 Sep 2026). It used to leave a
 * "comment", which went into its own table and showed up in the studio
 * somewhere different from every other piece of client feedback — so a
 * client had two ways to say the same thing and no way to tell which one
 * Portal would act on. Tom: *"One feedback channel, not two."* What the
 * client writes here now becomes a real change request on the film or
 * podcast, with the second it refers to already attached. Observations that
 * are not requests still have the general-comment control above the
 * transcript.
 *
 * Where the transcript has timings, the margin carries a timecode against
 * each paragraph and a request records the second its passage was said — so
 * nobody has to type one. The timecodes sit in the margin from tablet width
 * up; a phone has no margin to spare, so there they appear on the note
 * itself instead of squeezing the words.
 */
export function TranscriptEditor({
  doc,
  editable,
  resetKey,
  onChange,
  comments,
  canComment,
  onComment,
  cues,
  cueText
}: TranscriptEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bubble, setBubble] = useState<Bubble | null>(null);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState('');
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [stamps, setStamps] = useState<Stamp[]>([]);
  const [openMarker, setOpenMarker] = useState<number | null>(null);
  /** Bumped whenever the rendered document may have moved. */
  const [measure, setMeasure] = useState(0);

  /*
   * The timings are read through refs, not closed over.
   *
   * `readSelection` below is handed to the editor when it is created, and the
   * editor is only rebuilt when `resetKey` changes — so a version of that
   * callback that captured `cues` would go on using whatever the value was at
   * creation. Refs keep one stable callback that always sees the current
   * timings, without making the editor tear itself down when they load.
   */
  const cuesRef = useRef<Cue[] | undefined>(cues);
  const cueTextRef = useRef<string | undefined>(cueText);
  cuesRef.current = cues;
  cueTextRef.current = cueText;

  /** The contextual actions, positioned against the current selection. */
  const readSelection = useCallback((instance: Editor) => {
    const container = containerRef.current;
    if (!container) return;
    const {
      from,
      to,
      empty
    } = instance.state.selection;
    if (empty || !instance.isFocused) {
      setBubble(null);
      setComposing(false);
      return;
    }
    const quote = instance.state.doc.textBetween(from, to, ' ').trim();
    if (!quote) {
      setBubble(null);
      return;
    }

    /* Where the passage sits in the readable text, so the request can be
       found again after the transcript changes. */
    const readable = paragraphTexts(instance);
    let offset = 0;
    let quoteStart = -1;
    for (const paragraph of readable) {
      const at = paragraph.indexOf(quote);
      if (at !== -1) {
        quoteStart = offset + at;
        break;
      }
      offset += paragraph.length + 1;
    }
    let struck = true;
    instance.state.doc.nodesBetween(from, to, node => {
      if (!node.isText) return;
      if (!node.marks.some(mark => mark.type.name === DELETION)) struck = false;
    });
    const box = container.getBoundingClientRect();
    const coords = instance.view.coordsAtPos(from);
    setBubble({
      top: coords.top - box.top - 44,
      left: Math.max(0, coords.left - box.left),
      quote,
      quoteStart,
      timing: timingForPassage(cuesRef.current, cueTextRef.current, quote),
      struck
    });
    setComposing(false);
  }, []);
  const editor = useEditor({
    extensions: [Document, Paragraph.configure({
      HTMLAttributes: {
        class: 'mb-5'
      }
    }), Text, History, Deletion, Insertion],
    content: doc,
    editable,
    editorProps: {
      attributes: {
        class: EDITOR_CLASS,
        'aria-label': 'Transcript'
      },
      /* Backspace and Delete never remove Portal's words. */
      handleKeyDown(view, event) {
        if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
        if (!view.editable) return false;
        const {
          state
        } = view;
        const {
          empty,
          from,
          to
        } = state.selection;
        const backspace = event.key === 'Backspace';
        const start = empty ? backspace ? from - 1 : from : from;
        const end = empty ? backspace ? from : from + 1 : to;
        if (start < 0 || end > state.doc.content.size || start >= end) return true;
        const tr = state.tr;
        if (trackDelete(state, tr, start, end)) {
          placeCaret(tr, start);
          view.dispatch(tr);
        }
        /* Handled either way: nothing is ever removed by a keypress. */
        return true;
      },
      /* Typed wording is marked as an addition. */
      handleTextInput(view, from, to, text) {
        if (!view.editable) return false;
        const state = view.state;
        const insertionType = state.schema.marks[INSERTION];
        const deletionType = state.schema.marks[DELETION];
        if (!insertionType) return false;
        const tr = state.tr;
        let at = from;

        /* Typing over a selection strikes it first, then adds alongside. */
        if (from !== to) {
          trackDelete(state, tr, from, to);
          at = tr.mapping.map(to);
        }
        tr.insertText(text, at, at);
        if (deletionType) tr.removeMark(at, at + text.length, deletionType);
        tr.addMark(at, at + text.length, insertionType.create());
        placeCaret(tr, at + text.length);
        view.dispatch(tr);
        return true;
      },
      /* Clicking struck wording puts it back. */
      handleClick(view, pos, event) {
        const target = event.target as HTMLElement | null;
        if (!target?.closest?.('[data-deletion]')) return false;
        if (!view.editable) return false;
        const deletionType = view.state.schema.marks[DELETION];
        if (!deletionType) return false;
        const range = getMarkRange(view.state.doc.resolve(pos), deletionType);
        if (!range) return false;
        const tr = view.state.tr.removeMark(range.from, range.to, deletionType);
        view.dispatch(tr);
        return true;
      }
    },
    onUpdate: ({
      editor: instance
    }) => {
      onChange(instance.getJSON() as TranscriptDoc);
      setMeasure(value => value + 1);
    },
    onSelectionUpdate: ({
      editor: instance
    }) => {
      readSelection(instance);
    }
  }, [resetKey]);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  /* Markers and timecodes are measured from the rendered document, so each
     sits beside its own paragraph and stays there as the page scrolls. */
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!editor || !container) return;
    const paragraphs = paragraphTexts(editor);
    const positions: number[] = [];
    editor.state.doc.forEach((_node, offset) => positions.push(offset + 1));
    const grouped = new Map<number, TranscriptComment[]>();
    comments.forEach(comment => {
      const index = paragraphFor(paragraphs, comment);
      if (index < 0 || index >= positions.length) return;
      const list = grouped.get(index) ?? [];
      list.push(comment);
      grouped.set(index, list);
    });
    const box = container.getBoundingClientRect();

    /** Where a paragraph sits on screen, or null when it cannot be measured. */
    const topOf = (index: number): number | null => {
      try {
        return editor.view.coordsAtPos(positions[index]).top - box.top;
      } catch {
        return null;
      }
    };
    const nextMarkers: Marker[] = [];
    grouped.forEach((list, index) => {
      const top = topOf(index);
      if (top !== null) nextMarkers.push({
        paragraph: index,
        top,
        comments: list
      });
    });
    setMarkers(nextMarkers);
    if (!cues?.length || !cueText) {
      setStamps([]);
      return;
    }
    const nextStamps: Stamp[] = [];
    paragraphs.forEach((paragraph, index) => {
      if (!paragraph.trim()) return;
      const timing = timingForPassage(cues, cueText, paragraph);
      if (!timing) return;
      const top = topOf(index);
      if (top !== null) nextStamps.push({
        paragraph: index,
        top,
        label: timing.label
      });
    });
    setStamps(nextStamps);
  }, [editor, comments, measure, resetKey, cues, cueText]);
  useEffect(() => {
    const onResize = () => setMeasure(value => value + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  async function submit() {
    if (!bubble || !body.trim() || sending) return;
    setSending(true);
    setFailed('');
    const ok = await onComment({
      quote: bubble.quote,
      quoteStart: bubble.quoteStart,
      quoteSeconds: bubble.timing?.seconds ?? null,
      body: body.trim()
    });
    setSending(false);
    if (!ok) {
      setFailed('Not sent — try again.');
      return;
    }
    setBody('');
    setComposing(false);
    setBubble(null);
  }
  function deleteSelection() {
    if (!editor) return;
    const {
      from,
      to
    } = editor.state.selection;
    const tr = editor.state.tr;
    if (trackDelete(editor.state, tr, from, to)) {
      placeCaret(tr, from);
      editor.view.dispatch(tr);
    }
    setBubble(null);
  }
  function restoreSelection() {
    if (!editor) return;
    const deletionType = editor.state.schema.marks[DELETION];
    if (!deletionType) return;
    const {
      from,
      to
    } = editor.state.selection;
    editor.view.dispatch(editor.state.tr.removeMark(from, to, deletionType));
    setBubble(null);
  }
  const open = markers.find(marker => marker.paragraph === openMarker);
  return <div ref={containerRef} className="relative pr-10 sm:pr-24" data-id="element-1814" data-mp="24rjmt48bgyxo">
      <EditorContent editor={editor} data-id="element-1815" data-mp="24pp1hot4v4t2" />

      {/* Timecodes, in the margin beside their own paragraph. Hidden on a
          phone, which has no margin to give up without squeezing the words. */}
      {stamps.map(stamp => <span key={`t-${stamp.paragraph}`} style={{
      top: stamp.top
    }} aria-hidden="true" className="label-sm absolute right-10 hidden tabular-nums text-mute sm:block" data-id="element-1816" data-mp="19kkdr9vzv37n">
          {stamp.label}
        </span>)}

      {/* Contextual actions, beside the selection. */}
      {bubble && editable && !composing && <div style={{
      top: bubble.top,
      left: bubble.left
    }} className="absolute z-20 flex items-center gap-5 border border-ink bg-paper px-4 py-2 shadow-sm" data-id="element-1817" data-mp="uoux7r9kviab">
          {bubble.timing && <span className="label-sm tabular-nums text-mute" data-id="element-1818" data-mp="1ikfudu8bjqf2">{bubble.timing.label}</span>}
          {canComment && <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => setComposing(true)} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-1819" data-mp="1dkl3vrsgh2uj">
              Ask for a change
            </button>}
          {bubble.struck ? <button type="button" onMouseDown={event => event.preventDefault()} onClick={restoreSelection} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-1820" data-mp="2ik9kfdtx98gy">
              Restore
            </button> : <button type="button" onMouseDown={event => event.preventDefault()} onClick={deleteSelection} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-1821" data-mp="2xilgpjdduf7l">
              Delete
            </button>}
        </div>}

      {/* The request box, beside the passage it is about. */}
      {bubble && composing && <div style={{
      top: bubble.top,
      left: bubble.left
    }} className="absolute z-30 w-[22rem] max-w-[90vw] border border-ink bg-paper p-5 shadow-sm" data-id="element-1822" data-mp="3ga4jpq7ept9m">
          {bubble.timing && <span className="label-sm mb-3 block tabular-nums text-mute" data-id="element-1823" data-mp="2vh4ppqerdpje">
              At {bubble.timing.label}
            </span>}
          <p className="m-0 border-l-2 border-rule pl-3 font-display text-base leading-snug text-graphite" data-id="element-1824" data-mp="359aekr9prqud">
            “{bubble.quote.length > 120 ? `${bubble.quote.slice(0, 120)}…` : bubble.quote}”
          </p>
          <textarea value={body} onChange={event => setBody(event.target.value)} rows={3} autoFocus placeholder="What needs to change here?" className="mt-4 w-full resize-none border border-rule bg-paperDeep px-3 py-2 font-display text-lg leading-snug text-charcoal placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-1825" data-mp="3e8p5tsxpg5fx" />
          <div className="mt-3 flex flex-wrap items-center gap-4" data-id="element-1826" data-mp="1tuojob5ir0b2">
            <button type="button" onClick={submit} disabled={!body.trim() || sending} className="label border border-ink px-4 py-2 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper disabled:border-rule disabled:text-mute disabled:hover:bg-transparent disabled:hover:text-mute" data-id="element-1827" data-mp="1ovbsxfbj1937">
              {sending ? 'Sending…' : 'Send to Portal'}
            </button>
            <button type="button" onClick={() => setComposing(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-1828" data-mp="3vpkvod35s3r2">
              Cancel
            </button>
            {failed && <p role="alert" className="label-sm m-0 text-ink" data-id="element-1829" data-mp="37urwtspeg7ga">
                {failed}
              </p>}
          </div>
        </div>}

      {/* Margin markers, one per commented paragraph. */}
      {markers.map(marker => <button key={marker.paragraph} type="button" style={{
      top: marker.top
    }} onClick={() => setOpenMarker(openMarker === marker.paragraph ? null : marker.paragraph)} aria-label={`${marker.comments.length} note${marker.comments.length === 1 ? '' : 's'} on this passage`} className="absolute right-0 flex items-center gap-1 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-1830" data-mp="1b16jfwk5zjnw">
          <MessageSquareIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-1831" data-mp="3g6i9b4fgc162" />
          {marker.comments.length > 1 && <span className="label-sm" data-id="element-1832" data-mp="36a569fy0oayn">{marker.comments.length}</span>}
        </button>)}

      {/* The notes on one passage. */}
      {open && <div style={{
      top: open.top
    }} className="absolute right-8 z-30 w-[22rem] max-w-[90vw] border border-ink bg-paper p-5 shadow-sm" data-id="element-1833" data-mp="328tplzx4f02l">
          <div className="flex items-baseline justify-between gap-4" data-id="element-1834" data-mp="10g5gxb1pjkff">
            <span className="label text-graphite" data-id="element-1835" data-mp="vidg1rj34nz3">Notes</span>
            <button type="button" onClick={() => setOpenMarker(null)} className="label-sm text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-1836" data-mp="vivgz1gozqdc">
              Close
            </button>
          </div>
          <ul className="m-0 mt-3 list-none p-0" data-id="element-1837" data-mp="1cshc2q8r1oa4">
            {open.comments.map(comment => <li key={comment.id} className="border-t border-rule py-4" data-id="element-1838" data-mp="1hphij29igt1r">
                <p className="m-0 font-display text-lg leading-snug text-charcoal" data-id="element-1839" data-mp="k3ad5q5duwoc">{comment.body}</p>
                <span className="label-sm mt-2 block text-mute" data-id="element-1840" data-mp="p1bxsfigj41x">
                  {stampOf(comment) && <span className="tabular-nums" data-id="element-1841" data-mp="u0ostnjhfhfl">{stampOf(comment)} · </span>}
                  {comment.authorName} · {formatEditStamp(comment.at)}
                </span>
              </li>)}
          </ul>
        </div>}
    </div>;
}